import { createContext, PropsWithChildren, useContext, useMemo } from "react";

import { AppLoader } from "~/components/_layout/AppLoader";
import { api } from "~/utils/api";

type UserContextState = {
  userClerkId: string;
  userEmail: string;
  userFirstName: string;
  userLastName: string;
  userId: string;
  userProfileImage: string;
};

const UserContext = createContext<UserContextState | undefined>(undefined);

const normalizeStringValue = (input?: string | null) => input ?? "";

export const UserProvider = (props: PropsWithChildren): JSX.Element => {
  const currentUser = api.users.current.useQuery();

  const context = useMemo<UserContextState>(
    () => ({
      userClerkId: normalizeStringValue(currentUser.data?.clerkId),
      userEmail: normalizeStringValue(currentUser.data?.emailAddress),
      userFirstName: normalizeStringValue(currentUser.data?.firstName),
      userLastName: normalizeStringValue(currentUser.data?.lastName),
      userId: normalizeStringValue(currentUser.data?.id),
      userProfileImage: normalizeStringValue(currentUser.data?.profileImageUrl),
    }),
    [currentUser.data],
  );

  const isLoading = currentUser.isPending;
  if (isLoading) {
    return <AppLoader label="Loading your User details" isLoading={isLoading} />;
  }

  return <UserContext.Provider value={context}>{props.children}</UserContext.Provider>;
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("userUser must be used within a UserContextProvider");
  }
  return context;
};
